import router from 'next/router'
import React, { useState } from 'react'

import {
  chakra,
  Flex,
  IconButton,
  useColorModeValue,
  useColorMode,
  Text,
  Heading,
  Box,
  keyframes,
  useDisclosure,
} from '@chakra-ui/react'

import { FaPlay } from 'react-icons/fa'
import { SIGN_IN, SIGN_UP } from '@/lib/constants/authConstants'

import CustomImage from '@/components/Image/CustomImage'
import { basicTextColor, basicTextRgba } from '@/lib/constants/colorConstants'
import NavBar from '@/components/home/NavBar'
import { navMenusLan } from '@/lib/constants/navMenuConstants'
import CustomButton from '../Button'
import PlayVideo from '../VideoModal/PlayVideo'
import { motion } from 'framer-motion'

const animationKeyframes = keyframes`
  0% { transform: scale(1) rotate(0); border-radius: 50%; }
  25% { transform: scale(1.2) rotate(0); border-radius: 50%; }
  50% { transform: scale(1.2) rotate(0); border-radius: 50%; }
  75% { transform: scale(1) rotate(0); border-radius: 50%; }
  100% { transform: scale(1) rotate(0); border-radius: 50%; }
`

const animation = `${animationKeyframes} 4s ease-in-out infinite`

type Menu = {
  name: string
  path: string
}

interface IProps {
  menus: Menu[]
  canStick: boolean
  triggerNav: boolean
  heroText: {
    paragraph: string
    heading1: string
    heading2: string
  }
}

export const Hero = ({ heroText, triggerNav }: IProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure()

  const { toggleColorMode: toggleMode, colorMode } = useColorMode()
  const [video, setVideo] = useState<any>(false)

  const bg = useColorModeValue('white', 'black')

  const handlePlayVideo = () => {
    setVideo(true)
    onOpen()
  }

  const goToPage = (page: string) => {
    const host =
      process.env.NEXT_PUBLIC_VERCEL_ENV === 'production'
        ? process.env.NEXT_PUBLIC_APP_HOST
        : process.env.NEXT_PUBLIC_APP_LOCALHOST

    if (page === SIGN_IN) {
      return router.push(host + '/login')
    }
    return router.push(host + '/signup')
  }

  return (
    <chakra.div
      id="home"
      height={{ base: 'auto', md: '100vh' }}
      w="full"
      bg={bg}
      pb="12rem"
      transition="background 0.2s"
      overflowY="hidden"
      position="relative"
      //bgClip={}
      bgImage={{
        base: 'none',
        md: 'url("lanherobg.svg")',
      }}
      //backgroundColor={['primaryRgba', 'none']}
      bgRepeat="no-repeat, no-repeat"
      bgSize="100%"
      bgPosition="center right, top left 45px"
    >
      <NavBar canStick={triggerNav} menus={navMenusLan} />
      <Flex
        w="full"
        mx="auto"
        maxW="1200px"
        px="6"
        mt={{ base: '6.43rem', md: '8.43rem' }}
        alignItems="center"
        justifyContent="space-between"
        backgroundRepeat="no-repeat"
      >
        <Flex flexDir="column" width="full">
          <Flex
            flexDirection="column"
            flexWrap="wrap"
            // maxW={{ base: 'container.sm', md: '30.5rem' }}
            alignItems={{ base: 'center', md: 'start' }}
          >
            <Heading
              as="h1"
              textAlign={{ base: 'center', md: 'start' }}
              fontSize={{ base: '2xl', md: '2.62rem' }}
              fontWeight="semibold"
              color={basicTextColor[colorMode]}
            >
              {heroText.heading1}
              <Text color="primary" mt="1rem">
                {heroText.heading2}
              </Text>
            </Heading>
            <Text
              mt="1.43rem"
              maxW={{ base: 'container.sm', md: '28rem' }}
              textAlign={{ base: 'center', md: 'justify' }}
              fontSize={{ base: 'sm', md: '1.37rem' }}
              fontWeight="400"
              color={basicTextRgba[colorMode]}
              wordBreak="break-all"
            >
              {heroText.paragraph}
            </Text>
          </Flex>
          <Flex
            alignItems="center"
            justifyContent={{ base: 'center', md: 'start' }}
            mt="14"
          >
            <CustomButton
              mr="1.81rem"
              w="13.25rem"
              h="3.5rem"
              onClick={() => goToPage(SIGN_UP)}
            >
              Get Started
            </CustomButton>
            <Flex
              w={{ base: '4.31rem', md: '5.31rem' }}
              h={{ base: '4.31rem', md: '5.31rem' }}
              border="1px"
              borderColor="primaryRgba"
              rounded="full"
            >
              <Flex
                mx="auto"
                my="auto"
                alignItems="center"
                justifyContent="center"
                w={{ base: '3.31rem', md: '4.37rem' }}
                h={{ base: '3.31rem', md: '4.37rem' }}
                border="1px"
                borderColor="primaryRgba"
                rounded="full"
                as={motion.div}
                animation={animation}
              >
                <Flex
                  mx="auto"
                  my="auto"
                  alignItems="center"
                  justifyContent="center"
                  w={{ base: '2.31rem', md: '3.43rem' }}
                  h={{ base: '2.31rem', md: '3.43rem' }}
                  border="1px"
                  borderColor="primaryRgba"
                  rounded="full"
                >
                  <IconButton
                    //zIndex={1}
                    onClick={handlePlayVideo}
                    w={{ base: '2.31rem', md: '2.43rem' }}
                    h={{ base: '2.31rem', md: '2.43rem' }}
                    rounded="full"
                    aria-label="Play button"
                    bg={colorMode === 'light' ? 'primary' : 'primaryDark'}
                    color="white"
                    icon={<FaPlay />}
                    _hover={{ bg: 'primaryDark' }}
                    _active={{
                      bgColor: 'primaryLight',
                    }}
                  />
                  {video && (
                    <PlayVideo
                      isOpen={isOpen}
                      onClose={onClose}
                      onClick={onClose}
                    />
                  )}
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
        <Box display={{ base: 'none', md: 'flex' }}>
          <CustomImage src="/lanhero.svg" width={426} height={310} />
        </Box>
      </Flex>
    </chakra.div>
  )
}
